/**
 * Shows and exhibitions: entries, results and season summaries. A scratch is not last place,
 * reported results stay distinct from official ones, and points from different systems are never
 * added together. Pure: shared by the facet, the server and the web.
 */
import type { FacetEvent, Formatters, Quantity, Translate } from "../../../contract";

export const SHOW_OUTCOMES = {
  placed: "Placed", award: "Award", shown: "Shown, not placed", scratched: "Scratched", disqualified: "Disqualified",
} as const;
export type ShowOutcome = keyof typeof SHOW_OUTCOMES;

export const RESULT_STATUS = { reported: "As told", official: "Official" } as const;
export type ResultStatus = keyof typeof RESULT_STATUS;

export const SHOW_MEASURES = {
  weight: "Weight", hip_height: "Hip height", scrotal: "Scrotal circumference", frame: "Frame score",
  horn_ttt: "Horn tip to tip", horn_total: "Total horn", horn_composite: "Horn composite", other: "Other measurement",
} as const;
export type ShowMeasure = keyof typeof SHOW_MEASURES;

export const MEASURE_BASIS = { actual: "Measured", estimated: "Estimated", reconstructed: "Reconstructed" } as const;

export interface ShowMeasurement { measure: ShowMeasure; label?: string; value: Quantity; basis?: keyof typeof MEASURE_BASIS; official?: boolean }

export interface ShowEntryData {
  show_name: string; class_name?: string; division?: string; entry_number?: string; exhibitor?: string;
  measurements?: ShowMeasurement[]; receipt?: string; notes?: string;
}

export interface ShowResultData {
  show_name: string; class_name?: string; division?: string; outcome: ShowOutcome; placing?: number; award?: string; class_size?: number;
  judge?: string; points?: number; points_system?: string; status?: ResultStatus; evidence?: string; measurements?: ShowMeasurement[]; notes?: string;
}

export const SHOW_TYPES = ["operations.show_entry", "operations.show_result"];

export interface ShowLine {
  event_id: string;
  root_id: string;
  kind: "entry" | "result";
  on: string | null;
  show_name: string;
  job_id: string | null;
  class_name?: string;
  division?: string;
  entry_number?: string;
  exhibitor?: string;
  outcome?: ShowOutcome;
  placing?: number;
  award?: string;
  class_size?: number;
  judge?: string;
  points?: number;
  points_system?: string;
  status?: ResultStatus;
  measurements?: ShowMeasurement[];
}

export interface ShowGroup { key: string; show_name: string; job_id: string | null; on: string | null; entries: ShowLine[]; results: ShowLine[] }

export interface AnimalShowsState {
  shows: ShowGroup[];
  entries: number;
  results: number;
  wins: number;
  awards: number;
  last?: ShowLine;
}

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").trim();

/** One show is a job, or (for history without a job) a show name in a given year. */
export function showKey(line: { job_id: string | null; show_name: string; on: string | null }): string {
  return line.job_id ?? `${norm(line.show_name)}|${line.on?.slice(0, 4) ?? "?"}`;
}

export function lineFrom(e: { id: string; root_id?: string; type: string; occurred_at: string | null; job_id: string | null; data: Record<string, unknown> }): ShowLine {
  const d = e.data as unknown as ShowEntryData & ShowResultData;
  const base: ShowLine = {
    event_id: e.id, root_id: e.root_id ?? e.id, kind: e.type === "operations.show_entry" ? "entry" : "result",
    on: e.occurred_at ? e.occurred_at.slice(0, 10) : null, show_name: d.show_name, job_id: e.job_id,
  };
  for (const key of ["class_name", "division", "entry_number", "exhibitor", "outcome", "placing", "award", "class_size", "judge", "points", "points_system", "status", "measurements"] as const) {
    if (d[key] !== undefined) (base as unknown as Record<string, unknown>)[key] = d[key];
  }
  return base;
}

export function reduceAnimalShows(events: FacetEvent[]): AnimalShowsState | null {
  if (!events.length) return null;
  const groups = new Map<string, ShowGroup>();
  let last: ShowLine | undefined;
  for (const e of events) {
    const line = lineFrom(e);
    const key = showKey(line);
    const g = groups.get(key) ?? { key, show_name: line.show_name, job_id: line.job_id, on: line.on, entries: [], results: [] };
    if (line.on && (!g.on || line.on < g.on)) g.on = line.on;
    (line.kind === "entry" ? g.entries : g.results).push(line);
    groups.set(key, g);
    if (!last || (line.on ?? "") >= (last.on ?? "")) last = line;
  }
  const results = [...groups.values()].flatMap((g) => g.results);
  return {
    shows: [...groups.values()].sort((a, b) => (b.on ?? "").localeCompare(a.on ?? "")),
    entries: [...groups.values()].reduce((n, g) => n + g.entries.length, 0),
    results: results.length,
    wins: results.filter((r) => r.outcome === "placed" && r.placing === 1).length,
    awards: results.filter((r) => r.outcome === "award").length,
    ...(last ? { last } : {}),
  };
}

// ---------------------------------------------------------------- seasons

export interface SeasonSummary {
  year: string;
  shows: number;
  entries: number;
  results: number;
  wins: number;
  top3: number;
  awards: number;
  scratches: number;
  official: number;
  reported: number;
  /** Points per named system; null collects points recorded without a system. Never summed across systems. */
  points: Array<{ system: string | null; points: number }>;
}

export function seasonSummaries(lines: ShowLine[]): SeasonSummary[] {
  const byYear = new Map<string, ShowLine[]>();
  for (const l of lines) {
    const year = l.on?.slice(0, 4) ?? "?";
    byYear.set(year, [...(byYear.get(year) ?? []), l]);
  }
  return [...byYear.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([year, list]) => {
    const results = list.filter((l) => l.kind === "result");
    const points = new Map<string, { system: string | null; points: number }>();
    for (const r of results) {
      if (typeof r.points !== "number" || r.outcome === "scratched" || r.outcome === "disqualified") continue;
      const system = r.points_system?.trim() ? r.points_system.trim() : null;
      const key = system ? norm(system) : "";
      const cur = points.get(key) ?? { system, points: 0 };
      cur.points += r.points;
      points.set(key, cur);
    }
    return {
      year,
      shows: new Set(list.map(showKey)).size,
      entries: list.filter((l) => l.kind === "entry").length,
      results: results.length,
      wins: results.filter((r) => r.outcome === "placed" && r.placing === 1).length,
      top3: results.filter((r) => r.outcome === "placed" && (r.placing ?? 99) <= 3).length,
      awards: results.filter((r) => r.outcome === "award").length,
      scratches: results.filter((r) => r.outcome === "scratched").length,
      official: results.filter((r) => r.status === "official").length,
      reported: results.filter((r) => r.status !== "official" && r.outcome !== "scratched").length,
      points: [...points.values()].sort((a, b) => (a.system === null ? 1 : 0) - (b.system === null ? 1 : 0) || (a.system ?? "").localeCompare(b.system ?? "")),
    };
  });
}

/** "1st place", "22nd place"... localized through the catalog. */
export function placingText(n: number, t: Translate): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 13) return t("{{place}}th place", { place: n });
  if (mod10 === 1) return t("{{place}}st place", { place: n });
  if (mod10 === 2) return t("{{place}}nd place", { place: n });
  if (mod10 === 3) return t("{{place}}rd place", { place: n });
  return t("{{place}}th place", { place: n });
}

/** The headline of one result: "1st place · Class 4", "Reserve champion", "Scratched". */
export function resultHeadline(r: Partial<Pick<ShowResultData, "outcome" | "placing" | "award" | "class_name" | "class_size">>, x: Pick<Formatters, "t" | "count">): string {
  const cls = r.class_name;
  switch (r.outcome) {
    case "placed": {
      const place = typeof r.placing === "number" ? placingText(r.placing, x.t) : x.t("Placed");
      const of = typeof r.class_size === "number" && typeof r.placing === "number" ? x.count(r.class_size, "of {{count}}", "of {{count}}") : null;
      return [of ? `${place} ${of}` : place, cls].filter(Boolean).join(" · ");
    }
    case "award": return [r.award ?? x.t("Award"), cls].filter(Boolean).join(" · ");
    case "shown": return cls ? x.t("Shown in {{class}}, not placed", { class: cls }) : x.t("Shown, not placed");
    case "scratched": return cls ? x.t("Scratched from {{class}}", { class: cls }) : x.t("Scratched");
    case "disqualified": return cls ? x.t("Disqualified from {{class}}", { class: cls }) : x.t("Disqualified");
    default: return cls ?? "";
  }
}
